import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import ProductPanelService from '../../../../../services/product/ProductPanelService';

import {
  PRODUCT_LIST_ANIMATION,
  SharedProductItemProps,
  formatProductPrice,
  getStockColor,
  getStockLabel,
} from './POSProductShared';

import POSProductAddButton from './POSProductAddButton';

const POSProductListItem = ({
  product,
  disabled = false,
  onAddToCart,
}: SharedProductItemProps) => {
  // =====================================================
  // 🔹 Image
  // =====================================================

  const [imageUrl, setImageUrl] = useState('');

  // =====================================================
  // 🔹 Effects
  // =====================================================

  useEffect(() => {
    const loadImage = async () => {
      if (!product.imagePath) return;

      try {
        const url = await ProductPanelService.getProductImage(
          product.imagePath
        );

        if (url) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('❌ Error loading product image:', error);
      }
    };

    loadImage();
  }, [product.imagePath]);

  // Producto "por receta" en modo consumo directo: no controla stock terminado.
  const sellByRecipe = !!product.sellByRecipe;

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <motion.div
      layout
      {...PRODUCT_LIST_ANIMATION}
      className="
      overflow-hidden

      border
      border-stroke/70

      bg-whiten

      p-4

      transition-all
      duration-200

      hover:border-primary/20
      hover:bg-white
      hover:shadow-sm

      dark:border-strokedark
      dark:bg-boxdark-2
      dark:hover:border-primary/30
      dark:hover:bg-black

      sm:flex
      sm:items-center
      sm:gap-4
    "
    >
      {/* ===================================================== */}
      {/* Mobile */}
      {/* ===================================================== */}

      <div className="flex gap-3 sm:hidden">
        {/* Imagen */}

        <div
          className="
          flex
          h-16
          w-16
          shrink-0
          items-center
          justify-center

          overflow-hidden

          rounded-xl

          border
          border-stroke

          bg-white

          shadow-sm

          dark:border-strokedark
          dark:bg-boxdark
        "
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-xs text-body">IMG</span>
          )}
        </div>

        {/* Información */}

        <div
          className="
          min-w-0
          flex-1

          py-1
        "
        >
          <h4
            className="
            break-words

            text-sm
            font-semibold

            text-black

            dark:text-white
          "
          >
            {product.name}
          </h4>

          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-body">Precio</span>

              <span className="text-base font-bold text-primary">
                {formatProductPrice(product.price)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-body">Stock</span>

              <span
                className={`
                inline-flex
                items-center

                rounded-full

                px-2.5
                py-1

                text-xs
                font-semibold

                ${
                  sellByRecipe
                    ? 'bg-primary/10 text-primary'
                    : product.stockQuantity > 0
                    ? 'bg-success/10 text-success'
                    : 'bg-danger/10 text-danger'
                }
              `}
              >
                {sellByRecipe
                  ? getStockLabel(product.stockQuantity, true)
                  : `${product.stockQuantity} • ${getStockLabel(
                      product.stockQuantity
                    )}`}
              </span>
            </div>
          </div>

          <div className="mt-4">
            <POSProductAddButton
              disabled={disabled}
              onClick={() => onAddToCart?.(product)}
            />
          </div>
        </div>
      </div>

      {/* ===================================================== */}
      {/* Desktop */}
      {/* ===================================================== */}

      <div className="hidden w-full items-center gap-4 sm:flex">
        {/* Imagen */}

        <div
          className="
          flex
          h-14
          w-14
          shrink-0
          items-center
          justify-center

          overflow-hidden

          rounded-xl

          border
          border-stroke

          bg-white

          shadow-sm

          dark:border-strokedark
          dark:bg-boxdark
        "
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="
              h-full
              w-full
              object-cover
            "
            />
          ) : (
            <span
              className="
              text-[10px]
              font-medium

              text-bodydark2
            "
            >
              IMG
            </span>
          )}
        </div>

        {/* Nombre */}

        <div
          className="
          min-w-0
          flex-1

          py-1
        "
        >
          <h4
            className="
            truncate

            text-sm
            font-semibold

            text-black

            dark:text-white
          "
          >
            {product.name}
          </h4>

          <p
            className={`
            mt-1

            text-xs

            ${sellByRecipe ? 'text-primary' : 'text-body'}
          `}
          >
            {sellByRecipe ? 'Se vende por receta' : 'Stock disponible'}
          </p>
        </div>

        {/* Precio */}

        <div
          className="
          w-[110px]
          shrink-0

          text-right
        "
        >
          <p
            className="
            text-base
            font-bold

            text-primary
          "
          >
            {formatProductPrice(product.price)}
          </p>
        </div>

        {/* Stock */}

        <div
          className="
          w-[120px]
          shrink-0

          text-center
        "
        >
          <span
            className={`
            inline-flex
            items-center
            justify-center

            rounded-full

            px-2.5
            py-1

            text-xs
            font-semibold

            ${
              sellByRecipe
                ? 'bg-primary/10 text-primary'
                : product.stockQuantity > 0
                ? 'bg-success/10 text-success'
                : 'bg-danger/10 text-danger'
            }
          `}
          >
            {sellByRecipe ? 'Receta' : product.stockQuantity}
          </span>

          <div
            className={`
            mt-1

            text-xs

            ${getStockColor(product.stockQuantity, sellByRecipe)}
          `}
          >
            {getStockLabel(product.stockQuantity, sellByRecipe)}
          </div>
        </div>

        {/* Acción */}

        <div className="w-[120px] shrink-0">
          <POSProductAddButton
            size="sm"
            disabled={disabled}
            onClick={() => onAddToCart?.(product)}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default POSProductListItem;
